import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, uploadFile } from "../lib/api";
import { CrudActions } from "../components/Crud";
import BackButton from "../components/BackButton";
import AssessmentTools from "../components/AssessmentTools";
import ProjectBreakdown from "../components/ProjectBreakdown";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, FileText } from "lucide-react";

export default function ProjectDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [project, setProject] = useState(null);
    const [breakdown, setBreakdown] = useState(null);

    useEffect(() => {
        api.get("/projects").then(r => setProject(r.data.find(p => p.id === id)));
    }, [id]);

    if (!project) return <div className="p-6">Loading…</div>;

    const handleProgress = (bd) => {
        setBreakdown(bd);
        if (typeof bd?.progress === "number" && bd.progress !== project.progress) {
            setProject(p => ({ ...p, progress: bd.progress }));
        }
    };

    return (
        <div data-testid="project-detail-page">
            <BackButton to="/projects" label="All projects" />
            <div className="font-script text-[var(--brand-orange)] text-xl">{project.client_name || project.class_name || "project"}</div>
            <h1 className="font-serif text-4xl">{project.title}</h1>
            <div className="text-sm text-[var(--text-ink)]/65 mb-4">{project.description}</div>

            <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8 space-y-4">
                    <ProjectBreakdown projectId={id} onProgress={handleProgress} />
                </div>

                <div className="lg:col-span-4 space-y-5">
                    {user?.role === "student" && <ReferenceChecker projectId={id} />}
                    {user?.role === "student" && <AssessmentTools projectId={id} />}
                    <DocsBox projectId={id} />
                </div>
            </div>
        </div>
    );
}

function ReferenceChecker({ projectId }) {
    const [refs, setRefs] = useState([]);
    const [form, setForm] = useState({ style: "Harvard", source_type: "book", authors: "", title: "", year: "", publisher: "", journal: "", volume: "", issue: "", pages: "", url: "", accessed: "" });
    const [loading, setLoading] = useState(false);
    useEffect(() => { api.get("/references").then(r => setRefs(r.data.filter(x => x.project_id === projectId))); }, [projectId]);

    const generate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/ai/citation", form);
            const { data: saved } = await api.post("/references", { ...form, citation: data.citation, project_id: projectId });
            setRefs(x => [saved, ...x]);
        } finally { setLoading(false); }
    };
    const del = async (id) => { await api.delete(`/references/${id}`); setRefs(x => x.filter(r => r.id !== id)); };

    const exportBib = () => {
        const txt = refs.map((r, i) => `${i + 1}. ${r.citation}`).join("\n\n");
        const blob = new Blob([`Bibliography\n\n${txt}`], { type: "application/msword" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "bibliography.doc"; a.click();
    };

    return (
        <div className="card-warm p-5" data-testid="reference-checker">
            <div className="font-serif text-2xl mb-2 flex items-center gap-2"><Sparkles size={18} className="text-[var(--brand-orange)]" /> Reference Checker</div>
            <div className="grid grid-cols-2 gap-2">
                <select value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })}
                        data-testid="ref-style" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm">
                    {["APA", "MLA", "Harvard", "Chicago"].map(s => <option key={s}>{s}</option>)}
                </select>
                <select value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })}
                        data-testid="ref-type" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm">
                    {["book", "journal", "website", "newspaper", "video"].map(s => <option key={s}>{s}</option>)}
                </select>
                {[["authors", "Author(s)"], ["title", "Title"], ["year", "Year"], ["publisher", "Publisher"], ["journal", "Journal"], ["pages", "Pages"], ["url", "URL"], ["accessed", "Accessed"]].map(([k, l]) => (
                    <input key={k} placeholder={l} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                           data-testid={`ref-input-${k}`}
                           className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                ))}
            </div>
            <div className="flex gap-2 mt-3">
                <button onClick={generate} disabled={loading} data-testid="ref-generate-btn" className="btn-primary flex-1">
                    {loading ? "Generating…" : "Generate citation"}
                </button>
                {refs.length > 0 && <button onClick={exportBib} className="btn-ghost" data-testid="ref-export-btn">Export</button>}
            </div>
            <ul className="mt-4 space-y-2">
                {refs.map(r => (
                    <li key={r.id} className="flex gap-2 items-start text-sm card-warm p-3">
                        <span className="flex-1 whitespace-pre-wrap">{r.citation}</span>
                        <CrudActions idBase={`ref-${r.id}`} onDelete={() => del(r.id)} />
                    </li>
                ))}
            </ul>
        </div>
    );
}

function DocsBox({ projectId }) {
    const [docs, setDocs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");

    useEffect(() => { api.get("/documents").then(r => setDocs(r.data.filter(d => d.project_id === projectId))); }, [projectId]);
    const onFile = async (e) => {
        const f = e.target.files[0]; if (!f) return;
        setError(""); setUploading(true); setProgress(0);
        const fd = new FormData(); fd.append("file", f);
        try {
            const up = await uploadFile("/upload", fd, {
                onProgress: (p) => setProgress(p),
            });
            const { data } = await api.post("/documents", { name: up.name, mime: up.content_type, size: up.size, storage_path: up.path, url: up.url, project_id: projectId });
            setDocs(x => [data, ...x]);
        } catch (err) {
            setError(err.friendly || "Upload failed. Please try again.");
        } finally {
            setUploading(false); setProgress(0);
            e.target.value = "";
        }
    };
    const del = async (id) => { await api.delete(`/documents/${id}`); setDocs(x => x.filter(d => d.id !== id)); };
    return (
        <div className="card-warm p-5">
            <div className="font-serif text-2xl mb-2 flex items-center gap-2"><FileText size={18} /> Documents</div>
            <input type="file" onChange={onFile} disabled={uploading} data-testid="project-doc-upload" className="text-sm" />
            {uploading && (
                <div className="mt-2" data-testid="project-doc-progress">
                    <div className="flex justify-between text-xs mb-1">
                        <span>{progress < 100 ? "Uploading…" : "Processing document…"}</span>
                        <span className="tabular-nums">{progress}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden">
                        <div className="h-full brand-gradient transition-all duration-200" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            )}
            {error && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2 break-words" data-testid="project-doc-error">
                    {error}
                </div>
            )}
            <ul className="mt-3 space-y-1">
                {docs.map(d => (
                    <li key={d.id} className="flex items-center gap-2 text-sm">
                        <a href={d.url || d.data_url} target="_blank" rel="noreferrer" className="flex-1 truncate hover:underline">{d.name}</a>
                        <CrudActions idBase={`pd-${d.id}`} onDelete={() => del(d.id)} />
                    </li>
                ))}
            </ul>
        </div>
    );
}
